import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';

import { NewbuildingBrokerModalsContext } from './NewbuildingBrokerModalsContext';
import { useFallbackModalSettings, useMicrofrontAPI, useMicrofrontEvents } from './hooks';
import { TModalInvocationData, TModalType, TOpenMicrofrontResultHandler } from './types';
import { NewbuildingBrokerFallbackModal } from '../../components/NewbuildingBrokerFallbackModal';

/**
 * Компонент, обеспечивающий загрузку микрофронта модалок бронирования на клиенте.
 *
 * 1. Пользователь кликает на точку входа, и вызывается `startMicrofrontOpening`.
 * 2. Регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализируется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка.
 */
export const NewbuildingBrokerModalsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const invocationDataRef = React.useRef<TModalInvocationData | undefined>(undefined);
  const fallbackModalSettings = useFallbackModalSettings();
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const [loadingModalType, setLoadingModalType] = React.useState<TModalType | null>(null);

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setLoadingModalType(null);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // для DIY модалок снятие loading произойдет по событию из микрофронта.
        if (invocationDataRef.current?.type !== 'selfRequestModal') {
          setLoadingModalType(null);
        }
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(finishMicrofrontOpening);

  useMicrofrontEvents(microfrontManifest, setLoadingModalType);

  const startMicrofrontOpening = React.useCallback(
    (invocationData: TModalInvocationData) => {
      setLoadingModalType(invocationData.type);

      // Сохраняем в реф для работы с теми же параметрами на случай, если произойдет ошибка.
      invocationDataRef.current = invocationData;

      openMicrofront(invocationData);
    },
    [openMicrofront],
  );

  const handleRetry = React.useCallback(() => {
    startMicrofrontOpening(invocationDataRef.current as TModalInvocationData);
  }, [startMicrofrontOpening]);

  const value = React.useMemo(
    () => ({
      isSelfRequestModalLoading: loadingModalType === 'selfRequestModal',
      startMicrofrontOpening,
    }),
    [loadingModalType, startMicrofrontOpening],
  );

  return (
    <NewbuildingBrokerModalsContext.Provider value={value}>
      {children}
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          {...fallbackModalSettings}
          loading={Boolean(loadingModalType)}
          onClose={closeFallbackModal}
          onRetry={handleRetry}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </NewbuildingBrokerModalsContext.Provider>
  );
};
